import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, DollarSign, User } from 'lucide-react';

interface Kazanc {
  id: string;
  username: string;
  amount: number;
  game: string;
  createdAt: Date;
}

// 20 adet demo veri - SarLab'da gercek zamanli veri yok
const demoKazanclar: Kazanc[] = [
  { id: '1', username: 'Ahm***', amount: 125000, game: 'Sweet Bonanza', createdAt: new Date() },
  { id: '2', username: 'Meh***', amount: 89500, game: 'Gates of Olympus', createdAt: new Date() },
  { id: '3', username: 'Ali***', amount: 67200, game: 'Big Bass Bonanza', createdAt: new Date() },
  { id: '4', username: 'Can***', amount: 54800, game: 'Starlight Princess', createdAt: new Date() },
  { id: '5', username: 'Emu***', amount: 43200, game: 'Sugar Rush', createdAt: new Date() },
  { id: '6', username: 'Sel***', amount: 98700, game: 'The Dog House', createdAt: new Date() },
  { id: '7', username: 'Oya***', amount: 156000, game: 'Crazy Time', createdAt: new Date() },
  { id: '8', username: 'Ber***', amount: 72400, game: 'Lightning Roulette', createdAt: new Date() },
  { id: '9', username: 'Yus***', amount: 61800, game: 'Fruit Party', createdAt: new Date() },
  { id: '10', username: 'Cem***', amount: 88900, game: 'Wild West Gold', createdAt: new Date() },
  { id: '11', username: 'Den***', amount: 134500, game: 'Book of Dead', createdAt: new Date() },
  { id: '12', username: 'Kar***', amount: 47600, game: 'Reactoonz', createdAt: new Date() },
  { id: '13', username: 'Fat***', amount: 93200, game: 'Mega Ball', createdAt: new Date() },
  { id: '14', username: 'Gok***', amount: 78100, game: 'Monopoly Live', createdAt: new Date() },
  { id: '15', username: 'Hak***', amount: 112000, game: 'Gonzo Quest', createdAt: new Date() },
  { id: '16', username: 'Ibr***', amount: 56700, game: 'Starburst', createdAt: new Date() },
  { id: '17', username: 'Jas***', amount: 145800, game: 'Dead or Alive 2', createdAt: new Date() },
  { id: '18', username: 'Kor***', amount: 82300, game: 'Buffalo King', createdAt: new Date() },
  { id: '19', username: 'Lev***', amount: 69400, game: 'Wolf Gold', createdAt: new Date() },
  { id: '20', username: 'Mur***', amount: 178500, game: 'Jammin Jars', createdAt: new Date() },
];

const SonKazanclar: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [prevVisibleIds, setPrevVisibleIds] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  // Demo verileri kullan - SarLab'da gercek zamanli kazanc verisi yok
  useEffect(() => {
    // Simule loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Demo verileri kullan
  const displayData = useMemo(() => demoKazanclar, []);

  // visibleItems'ı useMemo ile hesapla - 6 öğe göster (yeni en üstte)
  // Key olarak sadece orijinal id kullan, böylece mevcut öğeler kayarken silinmez
  const visibleItems = useMemo(() => {
    const items: (Kazanc & { position: number; isNew: boolean })[] = [];
    const currentIds = new Set<string>();

    for (let i = 0; i < 6; i++) {
      const dataIndex = (currentIndex - i + displayData.length) % displayData.length;
      const item = displayData[dataIndex];
      currentIds.add(item.id);
      items.push({
        ...item,
        position: i,
        isNew: !prevVisibleIds.has(item.id)
      });
    }

    // Bir sonraki render için mevcut id'leri kaydet
    setPrevVisibleIds(currentIds);

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, displayData]);

  // Otomatik kaydırma - 0.2-0.7 saniye arası random
  useEffect(() => {
    const dataLength = displayData.length;

    const scheduleNext = () => {
      const randomDelay = Math.random() * 500 + 200; // 200-700ms arası
      return setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % dataLength);
        timeoutRef.current = scheduleNext();
      }, randomDelay);
    };

    const timeoutRef = { current: scheduleNext() };

    return () => clearTimeout(timeoutRef.current);
  }, [displayData.length]);

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 overflow-hidden h-full flex flex-col">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-xl font-bold text-white">Son Büyük Kazançlar</h2>
        <div className="ml-auto flex items-center space-x-1">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-green-400 text-xs">Canlı</span>
        </div>
      </div>

      <div ref={containerRef} className="relative flex-1 min-h-[336px] overflow-hidden">
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-14 bg-dark-700/50 rounded-lg" />
            ))}
          </div>
        ) : (
          <AnimatePresence initial={false} mode="popLayout">
            {visibleItems.map((kazanc) => (
              <motion.div
                key={kazanc.id}
                layout
                initial={kazanc.isNew ? { opacity: 0, y: -60, scale: 0.9 } : false}
                animate={{
                  opacity: kazanc.position === 5 ? 0.4 : 1,
                  y: kazanc.position * 56,
                  scale: 1,
                }}
                exit={{
                  opacity: 0,
                  y: 380,
                  scale: 0.9,
                  transition: { duration: 0.3 }
                }}
                transition={{
                  y: { type: "spring", stiffness: 350, damping: 30, mass: 0.8 },
                  opacity: { duration: 0.2 },
                  scale: { type: "spring", stiffness: 350, damping: 30 }
                }}
                className="absolute left-0 right-0 flex items-center justify-between p-3 bg-dark-700/30 rounded-lg hover:bg-dark-700/50 transition-colors"
                style={{ height: '52px' }}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{kazanc.username}</p>
                    <p className="text-gray-400 text-xs">{kazanc.game}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-1 text-green-400 font-bold">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">{kazanc.amount.toLocaleString('tr-TR')} ₺</span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default SonKazanclar;
